<template>
  <component :is="element" ref="rootEl" class="uia-menu uia-no-transition">
    <nav class="uia-nav">
      <ul ref="linkContainerEl" class="uia-root">
        <slot name="before-nav" />
        <li class="uia-link-container uia-mob-hide">
          <component
            :is="item.element || (item.dropdown ? 'button' : 'a')"
            v-for="(item, index) in menu"
            :key="index"
            :class="[
              'uia-link',
              item.attributes ? item.attributes.class : null,
              {
                'uia-has-dropdown': item.dropdown
              }
            ]"
            :data-dropdown="item.dropdown"
            :data-align="item.align"
            :aria-haspopup="item.dropdown && 'true'"
            :aria-expanded="item.dropdown && 'false'"
            tabindex="0"
            v-bind="item.attributes"
            v-on="item.listeners || {}"
          >
            <slot name="title" :item="item" :index="index">
              <span>{{ item.title }}</span>
            </slot>
          </component>
        </li>
        <slot name="after-nav" />
      </ul>
    </nav>
    <div class="uia-dropdown uia-mob-hide">
      <div ref="backgroundEl" class="uia-background">
        <div ref="backgroundAltEl" class="uia-background-alt" />
      </div>
      <div ref="arrowEl" class="uia-arrow" />
      <div ref="dropdownContainerEl" class="uia-dropdown-container">
        <div
          v-for="(item, index) in itemsWithDropdown"
          :key="index"
          class="uia-dropdown-section"
          :data-dropdown="item.dropdown"
          :data-align="item.align"
        >
          <div class="uia-dropdown-content">
            <slot :item="item" :index="index" />
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
  import { computed, watch, nextTick, onMounted } from 'vue'
  import { useDropdownMenu } from '@/composables/useDropdownMenu'

  const props = defineProps({
    menu: {
      type: Array,
      required: true
    },
    element: {
      type: String,
      default: 'header',
      validator: (val) => !!val
    },
    screenOffset: {
      type: [Number, String],
      default: 10,
      validator: (val) => +val >= 0
    },
    dropdownOffset: {
      type: [Number, String],
      default: 0,
      validator: (val) => +val >= 0
    },
    handler: {
      type: String,
      default: 'hover',
      validator: (val) => ['hover', 'click'].includes(val)
    },
    transitionTimeout: {
      type: [Number, String],
      default: 250
    },
    align: {
      type: String,
      default: 'center',
      validator: (val) => ['left', 'center', 'right'].includes(val)
    }
  })

  const emit = defineEmits(['open-dropdown', 'close-dropdown'])

  const {
    rootEl,
    linkContainerEl,
    dropdownContainerEl,
    backgroundEl,
    backgroundAltEl,
    arrowEl,
    elementsWithDropdown,
    openDropdown,
    closeDropdown,
    updateDataElements,
    registerDropdownElementsEvents,
    registerDropdownContainerEvents
  } = useDropdownMenu({
    handler: props.handler,
    align: props.align,
    screenOffset: props.screenOffset,
    dropdownOffset: props.dropdownOffset,
    transitionTimeout: props.transitionTimeout
  })

  const itemsWithDropdown = computed(() => {
    return props.menu.filter((item) => item.dropdown)
  })

  // Watch for changes
  watch(
    () => props.element,
    async () => {
      await nextTick()
      updateDataElements()
      registerDropdownElementsEvents()
    }
  )

  watch(
    () => props.handler,
    () => {
      registerDropdownElementsEvents(true)
      registerDropdownContainerEvents(true)
    }
  )

  watch(
    () => props.menu,
    async () => {
      await nextTick()
      updateDataElements()
      registerDropdownElementsEvents()
    },
    { deep: true }
  )

  // Initialize on mount with a delay to ensure DOM is ready
  onMounted(async () => {
    await nextTick()
    updateDataElements()
    registerDropdownElementsEvents()
    registerDropdownContainerEvents()
  })

  // Expose methods for programmatic control
  defineExpose({
    elementsWithDropdown,
    openDropdown,
    closeDropdown
  })
</script>
